import {
  CreateScanResponse,
  ScanByIdResponse,
  ScanResultOutcomeKind,
  ScanResultReferenceSpec,
  SearchScanResultsResponse,
  SearchScansResponse,
} from '../../core/openapi/bff-admin-api';
import {
  CreateScan,
  ScanById,
  ScanResultReference,
  SearchScanResult,
  SearchScanResultDevice,
  SearchScanResultOutcome,
  SearchScanResultsModel,
  SearchScans,
} from './scanner.model';

export class ScannerMapper {
  public static mapGetScanByIdResponse(response: ScanByIdResponse): ScanById {
    return {
      id: response.id,
      facility: {
        id: response.facility?.id ?? '',
        name: response.facility?.name ?? '-',
      },
      containers: (response.containers ?? []).map((container) => ({
        id: container.id,
        name: container.name,
        ipRange: container.ipRange,
      })),
      containersCount: response.containersCount ?? 0,
      foundDevicesCount: response.foundDevicesCount ?? 0,
      devicesWithoutModelCount: response.devicesWithoutModelCount ?? 0,
      scannedCount: response.scannedCount ?? 0,
      totalCount: response.totalCount ?? 0,
      isApprovable: response.isApprovable ?? false,
      status: {
        id: response.status?.id ?? 0,
        name: response.status?.name ?? '-',
      },
      createdDate: response.createdDate,
    };
  }

  public static mapCreateScanFromResponse(response: CreateScanResponse): CreateScan {
    return {
      ...response,
    };
  }

  public static mapSearchScansFromResponse(response: SearchScansResponse): SearchScans {
    return {
      id: response.id,
      facility: {
        id: response.facility?.id ?? '',
        name: response.facility?.name ?? '-',
      },
      containersCount: response.containersCount ?? 0,
      scanResultsCount: response.scanResultsCount ?? 0,
      createdByUser: {
        id: response.createdByUser?.id ?? '',
        firstName: response.createdByUser?.firstName ?? '-',
        lastName: response.createdByUser?.lastName ?? '-',
      },
      status: {
        id: response.status?.id ?? 0,
        name: response.status?.name ?? '-',
      },
      createdDate: response.createdDate,
    };
  }

  public static mapSearchScanResultsFromResponse(
    response: SearchScanResultsResponse,
  ): SearchScanResult {
    return {
      id: response.id,
      scanId: response.scanId,
      containerId: response.containerId ?? null,
      containerName: response.containerName ?? '-',
      ipAddress: response.ipAddress ?? '',
      scanResultId: response.scanResultId ?? null,
      equipmentId: response.equipmentId ?? null,
      macAddress: response.macAddress ?? null,
      model: response.model ? ScannerMapper.mapScanResultModel(response.model) : null,
      referenceSpec: response.referenceSpec
        ? ScannerMapper.mapScanResultReference(response.referenceSpec)
        : null,
      rawModel: response.rawModel ?? null,
      firmware: response.firmware ?? null,
      result: response.result
        ? ScannerMapper.mapScanResultOutcome(response.result)
        : ScannerMapper.getPendingOutcome(),
      previousDevice: response.previousDevice
        ? ScannerMapper.mapScanResultDevice(response.previousDevice)
        : null,
      isFinalized: response.isFinalized ?? false,
      detectedAtUtc: response.detectedAtUtc ?? null,
      finalizedAtUtc: response.finalizedAtUtc ?? null,
    };
  }

  public static mapScanResultDevice(response: {
    equipmentId?: string | null;
    ipAddress?: string | null;
    macAddress?: string | null;
    model?: { id: string; name: string } | null;
    rawModel?: string | null;
    firmware?: string | null;
    detectedAtUtc?: string | null;
  }): SearchScanResultDevice {
    return {
      equipmentId: response.equipmentId ?? null,
      ipAddress: response.ipAddress ?? null,
      macAddress: response.macAddress ?? null,
      model: response.model ? ScannerMapper.mapScanResultModel(response.model) : null,
      rawModel: response.rawModel ?? null,
      firmware: response.firmware ?? null,
      detectedAtUtc: response.detectedAtUtc ?? null,
    };
  }

  public static mapScanResultOutcome(response: {
    id: number;
    kind: ScanResultOutcomeKind;
    name: string;
  }): SearchScanResultOutcome {
    return {
      id: response.id,
      kind: response.kind,
      name: response.name ?? '-',
    };
  }

  /**
   * Структурный тип, а не DTO: тот же маппер вызывается и для REST-ответа, и для
   * SignalR-события, а у последнего в модели есть только id и name.
   */
  public static mapScanResultModel(response: {
    id: string;
    name: string;
    nominalHashrate?: number | null;
    hashrateUnitName?: string | null;
  }): SearchScanResultsModel {
    return {
      id: response.id,
      name: response.name,
      nominalHashrate: response.nominalHashrate ?? null,
      hashrateUnitName: response.hashrateUnitName ?? null,
    };
  }

  public static mapScanResultReference(response: ScanResultReferenceSpec): ScanResultReference {
    return {
      nominalHashrate: response.nominalHashrate ?? null,
      nominalHashrateInModelUnits: response.nominalHashrateInModelUnits ?? null,
      rawUnit: response.rawUnit ?? null,
      source: response.source ?? null,
    };
  }

  public static getPendingOutcome(): SearchScanResultOutcome {
    return {
      id: 4,
      kind: ScanResultOutcomeKind.PendingProcessing,
      name: 'PendingProcessing',
    };
  }
}
